import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { CheckCircle, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { PopupMessageWrapper } from './PopupMessageWrapper';
import { OpponentScoringHBH } from './OpponentScoringHBH';
import NumPad from './NumPad';
import CreateApi from '../Settings/appSettings';
import { useDataContext } from '../Context/useDataContext';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import { useLSContext } from '../Context/useLSContext';
import { useLSStates } from '../Hooks/useLSStates';
import { usePendingScoreContext } from '../Context/PendingScoreContext';
import {
    buildHoleEnqueueItems,
    isScoreCheckComplete,
} from '../utils/enqueueHoleScoreUpdates';

type EndOfRoundPhase = 'missing' | 'confirm';

interface EndOfRoundPopupProps {
    open: boolean;
    onClose: () => void;
    initialPhase: EndOfRoundPhase;
    scoreCheck: any[];
    setScoreCheck: (data: any[]) => void;
    newHoleSettings: (hole: number) => void;
    setRoundFollowUp: (open: boolean) => void;
    preview?: boolean;
}

function resolveInitialSelection(
    scoreCheck: any[],
    eventData: any,
    scoreCode: string,
    reducedRegistrations: any[],
): { hole: number; scoreCode: string } {
    const otherCode = reducedRegistrations.find((r) => r.scoreCode !== scoreCode)?.scoreCode;

    if (eventData?.scoreType?.toLowerCase() === 'opponent') {
        const markerEntry = scoreCheck.find((s: any) => s.markerScoreCode === scoreCode);
        if (markerEntry?.incompleteMarkerHoles?.length) {
            return { hole: markerEntry.incompleteMarkerHoles[0], scoreCode };
        }
        if (markerEntry?.incompletePlayerHoles?.length) {
            return {
                hole: markerEntry.incompletePlayerHoles[0],
                scoreCode: otherCode ?? scoreCode,
            };
        }
        const playerEntry = scoreCheck.find((s: any) => s.scoreCode === scoreCode);
        if (playerEntry?.incompletePlayerHoles?.length) {
            return { hole: playerEntry.incompletePlayerHoles[0], scoreCode };
        }
        if (playerEntry?.incompleteMarkerHoles?.length) {
            return {
                hole: playerEntry.incompleteMarkerHoles[0],
                scoreCode: otherCode ?? scoreCode,
            };
        }
    } else {
        const incomplete = scoreCheck.find((s: any) => s.incompletePlayerHoles?.length > 0);
        if (incomplete) {
            return {
                hole: incomplete.incompletePlayerHoles[0],
                scoreCode: incomplete.scoreCode ?? scoreCode,
            };
        }
    }

    return { hole: 1, scoreCode };
}

const MissingScoresBody = ({
    scoreCheck,
    eventData,
    scoreCode,
    appTheme,
}: {
    scoreCheck: any[];
    eventData: any;
    scoreCode: string;
    appTheme: any;
}) => {
    let incomplete: any[] = [];
    let markerIncomplete: any = {};
    let playerIncomplete: any = {};

    if (scoreCheck?.length > 0) {
        if (eventData.scoreType?.toLowerCase() === 'opponent') {
            markerIncomplete = scoreCheck.find((score: any) => score.markerScoreCode === scoreCode);
            playerIncomplete = scoreCheck.find((score: any) => score.scoreCode === scoreCode);
        } else {
            incomplete = scoreCheck.filter((score: any) => score.incompletePlayerHoles.length > 0);
        }
    }

    return (
        <Stack direction="column" spacing={0.5} width="100%" alignItems="center" sx={{ pb: '40px' }}>
            {markerIncomplete?._id && (
                <Stack
                    direction="column"
                    spacing={0}
                    sx={{
                        width: '88%',
                        maxWidth: 320,
                        padding: '8px 10px',
                        border: `1px dashed ${appTheme.palette.primary.main}`,
                        borderRadius: '5px',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                            Your Inputs
                        </Typography>
                        {markerIncomplete.incompletePlayerHoles.length === 0 &&
                            markerIncomplete.incompleteMarkerHoles.length === 0 && (
                                <CheckCircle color="success" />
                            )}
                    </Stack>
                    <Divider flexItem />
                    {markerIncomplete.incompletePlayerHoles.length > 0 && (
                        <Stack direction="column" spacing={0} width="100%">
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                                {markerIncomplete.playerName}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {markerIncomplete.incompletePlayerHoles.join(', ')}
                            </Typography>
                        </Stack>
                    )}
                    {markerIncomplete.incompleteMarkerHoles.length > 0 && (
                        <Stack direction="column" spacing={0} width="100%">
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                                You
                            </Typography>
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {markerIncomplete.incompleteMarkerHoles.join(', ')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
            {playerIncomplete?._id && (
                <Stack
                    direction="column"
                    spacing={0}
                    sx={{
                        width: '88%',
                        maxWidth: 320,
                        padding: '8px 10px',
                        border: `1px dashed ${appTheme.palette.primary.main}`,
                        borderRadius: '5px',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                            Opponent&apos;s Inputs
                        </Typography>
                        {playerIncomplete.incompletePlayerHoles.length === 0 &&
                            playerIncomplete.incompleteMarkerHoles.length === 0 && (
                                <CheckCircle color="success" />
                            )}
                    </Stack>
                    <Divider flexItem />
                    {playerIncomplete.incompletePlayerHoles.length > 0 && (
                        <Stack direction="column" spacing={0} width="100%">
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                                You
                            </Typography>
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {playerIncomplete.incompletePlayerHoles.join(', ')}
                            </Typography>
                        </Stack>
                    )}
                    {playerIncomplete.incompleteMarkerHoles.length > 0 && (
                        <Stack direction="column" spacing={0} width="100%">
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', fontWeight: 500, color: 'primary.dark' }}>
                                {markerIncomplete.playerName}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {playerIncomplete.incompleteMarkerHoles.join(', ')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
            {incomplete.length > 0 && (
                <Stack direction="column" spacing={0} width="100%">
                    <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                        The following holes are missing entries:
                    </Typography>
                    {incomplete.map((score: any) => (
                        <Stack key={score._id ?? score.playerName} direction="column" spacing={0} width="100%">
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {score.playerName}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ width: '100%', textAlign: 'left', color: 'primary.dark' }}>
                                {score.incompletePlayerHoles.join(', ')}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export const EndOfRoundPopup = ({
    open,
    onClose,
    initialPhase,
    scoreCheck,
    setScoreCheck,
    newHoleSettings,
    setRoundFollowUp,
    preview = false,
}: EndOfRoundPopupProps) => {
    const api = CreateApi();
    const auth = useAuth();
    const navigate = useNavigate();

    const { appTheme } = useLSStates();
    const { setScoreBlockID, scoreBlockID, setEndOfRoundPopupOpen } = useLSContext();
    const { eventData, scoreData, scoreCode, currentRound, scoreDataRef, registrationData, setScoreData, startHole } =
        useDataContext();
    const { holeSettings } = useHoleSettings();
    const { enqueue, flushQueue } = usePendingScoreContext();

    const [phase, setPhase] = useState<EndOfRoundPhase>(initialPhase);
    const [editingEnabled, setEditingEnabled] = useState(initialPhase === 'missing');
    const [selectedCell, setSelectedCell] = useState<{ hole: number; scoreCode: string } | null>(null);
    const [submissionCode, setSubmissionCode] = useState('');
    const [submissionCodeError, setSubmissionCodeError] = useState('');

    const recheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scoreDataForEnqueueRef = useRef(scoreData);
    const initializedRef = useRef(false);
    const returnHoleRef = useRef(holeSettings.hole);
    scoreDataForEnqueueRef.current = scoreData;

    const roundTotalHoles = useMemo(() => {
        const fromRound = Number(eventData?.rounds?.[currentRound - 1]?.totalHoles);
        const fromEvent = Number(eventData?.totalHoles);
        return (Number.isFinite(fromRound) && fromRound > 0 ? fromRound : 0)
            || (Number.isFinite(fromEvent) && fromEvent > 0 ? fromEvent : 0)
            || 18;
    }, [eventData?.rounds, eventData?.totalHoles, currentRound]);

    const reducedRegistrations = useMemo(() => {
        const deduplicated =
            registrationData?.reduce((acc: any, reg: any) => {
                if (!acc.some((item: any) => item.scoreCode === reg.scoreCode)) acc.push(reg);
                return acc;
            }, []) || [];
        return deduplicated.sort((a: any, b: any) => {
            if (a.scoreCode === scoreCode) return -1;
            if (b.scoreCode === scoreCode) return 1;
            return 0;
        });
    }, [registrationData, scoreCode]);

    const highScore = useMemo(() => {
        return eventData?.maxHoleScoreInfo?.overPar === 'par'
            ? holeSettings.par * 2
            : eventData?.maxHoleScoreInfo?.overPar
                ? holeSettings.par + eventData.maxHoleScoreInfo.overPar
                : 0;
    }, [eventData, holeSettings.par]);

    const allComplete = useMemo(
        () => isScoreCheckComplete(scoreCheck, eventData, scoreCode),
        [scoreCheck, eventData, scoreCode],
    );

    const hasSubmissionCode = eventData?.submissionCode && !preview;

    const confirmDisabled = Boolean(hasSubmissionCode && !submissionCode.trim());

    useEffect(() => {
        setEndOfRoundPopupOpen(open);
        return () => setEndOfRoundPopupOpen(false);
    }, [open, setEndOfRoundPopupOpen]);

    useEffect(() => {
        if (!open || !scoreBlockID || !editingEnabled) return;
        if (!reducedRegistrations.some((r: any) => r.scoreCode === scoreBlockID)) return;
        setSelectedCell((prev) => ({
            hole: prev?.hole ?? holeSettings.hole,
            scoreCode: scoreBlockID,
        }));
    }, [scoreBlockID, open, editingEnabled, reducedRegistrations, holeSettings.hole]);

    useEffect(() => {
        if (!open) {
            initializedRef.current = false;
            return;
        }
        setPhase(initialPhase);
        setEditingEnabled(initialPhase === 'missing');
        setSubmissionCode('');
        setSubmissionCodeError('');
    }, [open, initialPhase]);

    useEffect(() => {
        if (!open || initializedRef.current) return;

        returnHoleRef.current = holeSettings.hole > 0 ? holeSettings.hole : returnHoleRef.current;

        if (initialPhase === 'confirm') {
            initializedRef.current = true;
            const defaultHole = holeSettings.hole > 0 ? holeSettings.hole : 1;
            setSelectedCell({ hole: defaultHole, scoreCode });
            setScoreBlockID(scoreCode);
            return;
        }

        if (!scoreCheck?.length) return;

        initializedRef.current = true;
        const initial = resolveInitialSelection(scoreCheck, eventData, scoreCode, reducedRegistrations);
        setSelectedCell(initial);
        newHoleSettings(initial.hole);
        setScoreBlockID(initial.scoreCode);
    }, [open, initialPhase, scoreCheck, eventData, scoreCode, reducedRegistrations, holeSettings.hole, newHoleSettings, setScoreBlockID]);

    const runRecheck = useCallback(async () => {
        if (preview || !eventData?._id) return;
        await flushQueue();
        const response = await api.get('/ls/CheckCompleteScores', {
            params: {
                eventID: eventData._id,
                roundNumber: currentRound,
                scoreCode,
            },
        });
        setScoreCheck(response.data);
    }, [api, currentRound, eventData?._id, flushQueue, preview, scoreCode, setScoreCheck]);

    useEffect(() => {
        if (open) {
            void runRecheck();
        }
        return () => {
            if (recheckTimerRef.current) {
                clearTimeout(recheckTimerRef.current);
            }
        };
    }, [open]);

    const handleScoreChange = useCallback((updatedScoreData: any[]) => {
        scoreDataForEnqueueRef.current = updatedScoreData;
        const items = buildHoleEnqueueItems({
            scoreData: updatedScoreData,
            scoreDataRef,
            holeNumber: holeSettings.hole,
            currentRound,
            scoreCode,
            eventData,
            roundTotalHoles,
        });
        if (items.length > 0) {
            enqueue(items);
            void flushQueue();
        }

        if (recheckTimerRef.current) {
            clearTimeout(recheckTimerRef.current);
        }
        recheckTimerRef.current = setTimeout(() => {
            void runRecheck();
        }, 3000);
    }, [
        currentRound,
        enqueue,
        eventData,
        flushQueue,
        holeSettings.hole,
        roundTotalHoles,
        runRecheck,
        scoreCode,
        scoreDataRef,
    ]);

    const handleCellSelect = useCallback(
        (hole: number, cellScoreCode: string) => {
            if (!editingEnabled) return;
            setSelectedCell({ hole, scoreCode: cellScoreCode });
            if (hole !== holeSettings.hole) {
                newHoleSettings(hole);
            }
            setScoreBlockID(cellScoreCode);
        },
        [editingEnabled, holeSettings.hole, newHoleSettings, setScoreBlockID],
    );

    const handleContinue = () => {
        setPhase('confirm');
        setEditingEnabled(false);
    };

    const handleConfirm = async () => {
        if (preview) {
            onClose();
            return;
        }

        if (eventData?.submissionCode && eventData.submissionCode.toLowerCase() !== submissionCode.toLowerCase()) {
            setSubmissionCodeError('Invalid submission code. Please try again.');
            return;
        }

        await flushQueue();

        const scoreDataIDs = scoreData.map((score: any) => score._id);
        const teeTimeForPace: string | undefined = scoreData?.[0]?.roundScores?.[currentRound - 1]?.teeTime;
        const isOpponent = eventData?.scoreType?.toLowerCase() === 'opponent';
        const paceContext =
            (eventData as any)?._id && teeTimeForPace
                ? {
                    eventID: (eventData as any)._id as string,
                    roundNumber: currentRound,
                    teeTime: teeTimeForPace,
                    scoreCode: isOpponent ? scoreCode : null,
                    totalHoles: roundTotalHoles,
                }
                : undefined;

        const response = await api.post('/ls/ConfirmScores', {
            eventScoreIDs: scoreDataIDs,
            roundIndex: currentRound - 1,
            markerScoreCode: scoreCode,
            userID: auth.user?.profile?.sub || `scoreCode:${scoreCode}`,
            ...(paceContext ? { paceContext } : {}),
        });

        setScoreData(response.data);

        if (scoreData[0].roundScores.length > 1) {
            setRoundFollowUp(true);
            onClose();
        } else {
            newHoleSettings(startHole);
            onClose();
            navigate('/login/');
        }
    };

    const handleReturn = () => {
        const holeToRestore = returnHoleRef.current;
        if (holeToRestore > 0) {
            newHoleSettings(holeToRestore);
        }
        onClose();
    };

    if (!open) return null;

    return (
        <PopupMessageWrapper open={open} width={90} maxHeight={92}>
            <Stack
                direction="column"
                spacing={0}
                alignItems="center"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'calc(92dvh - 48px)',
                    overflowY: 'auto',
                    width: '100%',
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    sx={{ flexShrink: 0 }}
                >
                    <IconButton
                        onClick={handleReturn}
                        aria-label="Return"
                        sx={{ color: 'sepia.dark', flexShrink: 0 }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{ color: 'sepia.dark', fontWeight: 700, flex: 1, textAlign: 'center' }}
                    >
                        {phase === 'missing' ? 'Missing Scores' : 'Round Review Confirmation'}
                    </Typography>
                    <Box sx={{ width: 40, flexShrink: 0 }} />
                </Stack>

                <Box sx={{ overflowY: 'auto', width: '100%', flexShrink: 0 }}>
                    {phase === 'missing' ? (
                        <MissingScoresBody
                            scoreCheck={scoreCheck}
                            eventData={eventData}
                            scoreCode={scoreCode}
                            appTheme={appTheme}
                        />
                    ) : (
                        <Stack direction="column" spacing={0.5} width="100%" sx={{ pb: '40px' }}>
                            {hasSubmissionCode ? (
                                <>
                                    <Typography variant="body1" sx={{ color: 'sepia.main' }}>
                                        Please report to scoring to get your submission code.
                                    </Typography>
                                    {submissionCodeError && (
                                        <Typography variant="body1" color="error">
                                            {submissionCodeError}
                                        </Typography>
                                    )}
                                    <TextField
                                        label="Submission Code"
                                        value={submissionCode}
                                        onChange={(e) => setSubmissionCode(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'white',
                                                '& fieldset': { borderColor: 'sepia.extraLight' },
                                                '&:hover fieldset': { borderColor: 'sepia.light' },
                                                '&.Mui-focused fieldset': { borderColor: 'sepia.main' },
                                            },
                                        }}
                                    />
                                </>
                            ) : (
                                <Typography variant="body1" sx={{ color: 'sepia.main' }}>
                                    I confirm that the scores I am about to submit are accurate?
                                </Typography>
                            )}
                            {phase === 'confirm' && !allComplete && (
                                <Typography variant="body2" color="warning.main">
                                    Some scores are still incomplete. Enable Edit to fix them, or go back to review missing holes.
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                disabled={confirmDisabled}
                                onClick={() => void handleConfirm()}
                                sx={{
                                    mt: 0.5,
                                    backgroundColor: 'primary.light',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.main' },
                                    '&.Mui-disabled': {
                                        backgroundColor: 'sepia.extraLight',
                                        color: 'sepia.light',
                                    },
                                }}
                            >
                                Yes, count it!
                            </Button>
                        </Stack>
                    )}
                </Box>

            <Stack
                direction="column"
                spacing={0}
                alignItems="center"
                width="100%"
                sx={{ flexShrink: 0 }}
            >
                    {phase === 'confirm' && (
                        <Stack direction="row" width="100%" justifyContent="flex-end" sx={{ mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editingEnabled}
                                        onChange={(e) => setEditingEnabled(e.target.checked)}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label="Edit"
                                sx={{ ml: 0, mr: 0 }}
                            />
                        </Stack>
                    )}
                    <OpponentScoringHBH
                        allowEdit={false}
                        factor={1}
                        holeSettings={holeSettings}
                        highlightCurrentHole={false}
                        selectedCell={selectedCell ?? undefined}
                        onCellSelect={handleCellSelect}
                        editingEnabled={editingEnabled}
                        hideOuterBorder
                    />
                    {(phase === 'missing' || (phase === 'confirm' && editingEnabled)) && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'sepia.main',
                                textAlign: 'center',
                                width: '100%',
                                mt: 0.5,
                            }}
                        >
                            Tap a score to edit or update
                        </Typography>
                    )}
                    <Box sx={{ mt: '40px', width: '100%' }}>
                        <NumPad
                            highScore={highScore}
                            lowScore={1}
                            reducedRegistrations={reducedRegistrations}
                            disabled={!editingEnabled}
                            onScoreChange={handleScoreChange}
                            fullWidth
                        />
                    </Box>
                </Stack>

                {phase === 'missing' && allComplete && (
                <Stack direction="column" spacing={0.5} width="100%" sx={{ flexShrink: 0, pt: 0.5 }}>
                    <Button
                        variant="contained"
                        onClick={handleContinue}
                        sx={{
                            backgroundColor: 'primary.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'primary.main' },
                        }}
                    >
                        Continue
                    </Button>
                </Stack>
                )}
            </Stack>
        </PopupMessageWrapper>
    );
};
